import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import {
  lifecycleVisual,
  type LifecycleKind,
  lifecycleLegendItems,
} from "./lifecycle-status";

/** Colored indicator + label for a doc lifecycle value — plan/30. */
export function LifecycleStatus(props: {
  value: string | null | undefined;
  /**
   * @deprecated Labels always show (ERPNext-style). Kept for call-site compat.
   */
  showLabel?: boolean;
  /** When set, status opens this doc (same as clicking the number). */
  to?: string;
  className?: string;
}) {
  const visual = lifecycleVisual(props.value);
  if (!visual) {
    return (
      <span className={cn("text-[var(--color-muted)]", props.className)}>
        —
      </span>
    );
  }
  const body = (
    <>
      <span
        className={cn("size-1.5 shrink-0 rounded-full", visual.dotClassName)}
        aria-hidden
      />
      {visual.label}
    </>
  );
  const base = cn(
    "inline-flex items-center gap-1.5 text-xs font-medium text-[var(--color-foreground)]",
    props.className,
  );
  if (props.to) {
    return (
      <Link
        to={props.to}
        className={cn(
          base,
          "no-underline hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)]",
        )}
        title={visual.label}
      >
        {body}
      </Link>
    );
  }
  return (
    <span className={base} title={visual.label}>
      {body}
    </span>
  );
}

/** Compact legend for list pages — indicator + label. */
export function LifecycleLegend(props: {
  kinds: LifecycleKind[];
  className?: string;
}) {
  const items = lifecycleLegendItems(props.kinds);
  return (
    <ul
      className={cn(
        "m-0 flex list-none flex-wrap items-center gap-x-4 gap-y-1 p-0 text-xs text-[var(--color-muted)]",
        props.className,
      )}
      aria-label="Lifecycle legend"
    >
      {items.map((item) => (
        <li key={item.kind} className="inline-flex items-center gap-1.5">
          <span
            className={cn("size-1.5 shrink-0 rounded-full", item.dotClassName)}
            aria-hidden
          />
          <span>{item.label}</span>
        </li>
      ))}
    </ul>
  );
}
